/**
 * API Error Handling Service
 *
 * This module provides service-level error handling functionality for the Personal Trainer App API.
 * It focuses on AxiosError transformation, service integration, and application-specific error processing.
 * It complements the error types and utility functions defined in the types module.
 *
 * Features:
 * - AxiosError to ApiError transformation
 * - Service-specific error context enrichment
 * - Development logging utilities
 * - Laravel validation error parsing
 * - Error reporting preparation for monitoring services
 */

import { HTTP_STATUS } from '../../config/constants';
import { tierFromPlanName } from '../../config/subscription-plans';
import { InvalidDataError } from '../../errors/invalid-data.error';
import {
  ApiErrorClass,
  createApiError,
  getErrorCodeFromStatus,
  type ApiError,
  type ApiErrorResponse,
  type ConfirmationDetails,
  type PaywallDetails,
  type StaleDetails,
  type PaywallFeature,
  type UpgradePrompt,
  type ValidationErrorInfo,
  API_ERROR_CODES,
  type ApiErrorCode,
  type ErrorSeverity,
  type ErrorCategory,
} from '../../types/api/errors';
import { isAxiosError } from '../../types/api/request.types';
import { dayjs } from '../../utils/date';
import { getEnv } from '../../utils/env';

import type { ServiceErrorReport } from '../../types/api/error.types';
import type { ConfirmationMethod } from '../../types/auth';
import type { AxiosError } from 'axios';

// =============================================================================
// SERVICE-LAYER ERROR PASSTHROUGH
// =============================================================================

/**
 * rethrowTyped — the service-layer error passthrough helper.
 *
 * The request coordinator hands every non-auth failure to service callers as a
 * typed `ApiErrorClass` (exposing `.status`/`.code`/`.details`). Historically
 * each service `catch` re-wrapped that into a plain `new Error(...)`, discarding
 * the typed shape before it reached the store/hook/UI. This helper is the single
 * chokepoint that preserves it:
 *
 *   - an `ApiErrorClass` (incl. any 402 / future `PaymentRequiredError`) is
 *     re-thrown by identity, keeping `.status`/`.code`/`.details` intact;
 *   - an `InvalidDataError` (client-side validation) is re-thrown by identity;
 *   - only a genuinely untyped throw falls back to `new Error(fallbackMessage)`.
 *
 * The `ApiErrorClass` guard MUST come first: `InvalidDataError extends Error`,
 * not `ApiErrorClass` — they are a disjoint hierarchy.
 */
export function rethrowTyped(error: unknown, fallbackMessage: string): never {
  if (error instanceof ApiErrorClass) throw error;
  if (error instanceof InvalidDataError) throw error;
  throw new Error(fallbackMessage);
}

// =============================================================================
// BLOB ERROR BODIES
// =============================================================================

/**
 * Read a Blob error body back into the JSON envelope it carries.
 *
 * A file export asks for `responseType: 'blob'`, and axios applies that to a
 * failure too: the server's JSON error arrives wrapped in a Blob. The transform
 * below reads `code`, `message` and `meta` off the body and cannot see inside a
 * Blob, so a refused export kept axios's "Request failed with status code 402"
 * and lost its upgrade prompt. This unwraps the body in place, before the error
 * is typed, so a blob request fails exactly like a JSON one.
 *
 * Async because reading a Blob is, which is why it runs ahead of the
 * synchronous transform rather than inside it. A body that is not a JSON object
 * (an HTML gateway page, an unreadable Blob) is left as it arrived.
 */
export async function readBlobErrorBody(error: unknown): Promise<void> {
  if (!isAxiosError(error) || !error.response) return;

  const { data } = error.response;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return;

  try {
    const body: unknown = JSON.parse(await readBlobText(data));
    if (body !== null && typeof body === 'object' && !Array.isArray(body)) {
      error.response.data = body;
    }
  } catch {
    // Not JSON, or unreadable: there is no envelope to read, keep the Blob.
  }
}

/**
 * A Blob's contents as text. React Native's Blob has no `.text()`, but its
 * `FileReader` reads one.
 */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => resolve(String(reader.result ?? ''));
    reader.onerror = (): void =>
      reject(reader.error ?? new Error('Could not read the error body'));
    reader.readAsText(blob);
  });
}

// =============================================================================
// AXIOS ERROR TRANSFORMATION
// =============================================================================

/**
 * Transform an AxiosError into a standardized ApiError with enriched context.
 * Synchronous: a Blob body must be unwrapped first ({@link readBlobErrorBody}).
 */
export function transformAxiosToApiError(axiosError: AxiosError): ApiError {
  const response = axiosError.response;
  const request = axiosError.request;

  // Extract error details from response
  let errorCode: ApiErrorCode = API_ERROR_CODES.UNKNOWN_ERROR;
  let message = axiosError.message;
  let details: Record<string, unknown> = {};

  if (response) {
    // A response was received. Prefer a recognized server-provided error code
    // (the backend's stable ApiErrorCode envelope); otherwise derive from the
    // HTTP status. This runs even when the body is not a JSON object (empty,
    // string, or HTML — e.g. a non-Laravel gateway/proxy error) so those still
    // type by status instead of collapsing to BAD_REQUEST.
    const responseData =
      response.data && typeof response.data === 'object'
        ? (response.data as ApiErrorResponse)
        : undefined;

    if (
      responseData?.code &&
      Object.values(API_ERROR_CODES).includes(responseData.code as ApiErrorCode)
    ) {
      errorCode = responseData.code as ApiErrorCode;
    } else {
      errorCode = getErrorCodeFromStatus(response.status);
    }

    // Use the server-provided message when present. User-facing text is never
    // sourced from a debug field; the whole body is retained in details for
    // diagnostics only.
    if (responseData?.message) {
      message = responseData.message;
    }

    details = {
      ...responseData?.details,
      serverResponse: responseData ?? response.data,
      originalAxiosError: {
        code: axiosError.code,
        name: axiosError.name,
      },
    };

    // Add validation errors if present (the 422 { errors } envelope)
    if (response.status === 422 && responseData?.errors) {
      details.validationErrors = parseLaravelValidationErrors(
        responseData.errors
      );
    }

    // A refusal's reason (`meta.reason`) rides on any code; readers branch on
    // the code first, then on it.
    if (typeof responseData?.meta?.reason === 'string') {
      details.reason = responseData.meta.reason;
    }

    // Parse the 402 paywall meta envelope into a normalized details.paywall
    // (mirrors the 422 validationErrors parse). All fields are optional, so a
    // bare 402 with no meta still stamps an empty bag and types cleanly as a
    // PaymentRequiredError with the fields undefined.
    if (response.status === HTTP_STATUS.PAYMENT_REQUIRED) {
      details.paywall = parsePaywallMeta(responseData?.meta);
    }

    // Drift keys on the CODE, not the status. A 409 is ambiguous — on change
    // proposals alone it also answers a second pending proposal, an
    // unanswerable one and an already sent reminder — so only the code says
    // this one carries a diff to re-decide against.
    if (errorCode === API_ERROR_CODES.RESOURCE_STALE) {
      details.drift = parseStaleMeta(responseData?.meta);
    }

    // "Confirm it's you" keys on the code, which a bare 423 maps to as well:
    // its meta says which methods can confirm.
    if (errorCode === API_ERROR_CODES.CONFIRMATION_REQUIRED) {
      details.confirmation = parseConfirmationMeta(responseData?.meta);
    }
  } else if (request) {
    // Network error (no response received)
    errorCode = API_ERROR_CODES.NETWORK_ERROR;
    message = 'Network error: No response received from server';
    details = {
      originalAxiosError: {
        code: axiosError.code,
        name: axiosError.name,
      },
    };
  } else {
    // Request setup error
    errorCode = API_ERROR_CODES.BAD_REQUEST;
    message = axiosError.message || 'Request configuration error';
    details = {
      originalAxiosError: {
        code: axiosError.code,
        name: axiosError.name,
      },
    };
  }

  // Create the error using the factory function
  const apiError = createApiError(
    message,
    errorCode,
    response?.status,
    details
  );

  // Enrich with additional context from axios
  enrichApiErrorWithAxiosContext(apiError, axiosError);

  return apiError;
}

/**
 * Enrich an ApiError with context from AxiosError
 */
function enrichApiErrorWithAxiosContext(
  apiError: ApiError,
  axiosError: AxiosError
): void {
  const config = axiosError.config;
  const response = axiosError.response;

  if (config) {
    // Add request context (safely cast to add properties)
    const enrichedError = apiError as ApiError & {
      url?: string;
      method?: string;
      requestId?: string;
      requestStartedAt?: number;
      correlationId?: string;
    };

    enrichedError.url = config.url;
    enrichedError.method = config.method?.toUpperCase();
    enrichedError.requestId = config.metadata?.requestId;
    enrichedError.requestStartedAt = config.metadata?.startedAt;
  }

  if (response) {
    // Add response context
    const enrichedError = apiError as ApiError & {
      statusText?: string;
    };
    enrichedError.statusText = response.statusText;
  }

  // Add correlation ID for tracking if not present
  const enrichedError = apiError as ApiError & { correlationId?: string };
  if (!enrichedError.correlationId) {
    enrichedError.correlationId = generateCorrelationId();
  }
}

// =============================================================================
// PAYWALL (402) META PARSING
// =============================================================================

/**
 * Parse the backend 402 `meta` envelope into the normalized `PaywallDetails`
 * stamped onto `details.paywall`. Feature-agnostic: the backend authors the
 * copy, we only reshape it.
 *
 * Tier label-vs-value trap: `requiredTier` comes from the enum
 * `upgrade_prompt.target_plan` ("pro"), while `subscription.current` is a
 * display name ("Free") that must be mapped back via `tierFromPlanName`.
 */
/**
 * Normalize the `meta.drift` envelope into `details.drift`.
 *
 * Mirrors {@link parsePaywallMeta}. Returns `undefined` rather than an empty
 * bag when the shape is not there: a drift with no changes is not a drift, and
 * a caller rendering "here is what moved" over nothing would be worse than one
 * that falls back to the plain refusal message.
 */
export function parseStaleMeta(meta: unknown): StaleDetails | undefined {
  const drift = (meta as { drift?: unknown } | undefined)?.drift as
    Partial<StaleDetails> | undefined;

  if (!drift || typeof drift.summary !== 'string') return undefined;

  return {
    summary: drift.summary,
    changes: Array.isArray(drift.changes) ? drift.changes : [],
  };
}

export function parsePaywallMeta(meta: unknown): PaywallDetails {
  if (!meta || typeof meta !== 'object') return {};

  const envelope = meta as {
    subscription?: {
      current?: string;
      required?: string;
      upgrade_url?: string;
    };
    upgrade_prompt?: UpgradePrompt;
    feature?: PaywallFeature;
  };

  const upgradePrompt = envelope.upgrade_prompt;
  const currentName = envelope.subscription?.current;

  return {
    requiredTier: upgradePrompt?.target_plan,
    currentTier: currentName ? tierFromPlanName(currentName) : undefined,
    upgradePrompt,
    feature: envelope.feature,
    upgradeUrl: envelope.subscription?.upgrade_url,
  };
}

// =============================================================================
// CONFIRMATION REQUIRED (423) META PARSING
// =============================================================================

/** Every method this build can confirm with. */
const KNOWN_CONFIRMATION_METHODS: Record<ConfirmationMethod, true> = {
  password: true,
  google: true,
  apple: true,
};

const isConfirmationMethod = (value: unknown): value is ConfirmationMethod =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(KNOWN_CONFIRMATION_METHODS, value);

/**
 * Normalize a `CONFIRMATION_REQUIRED` refusal's `meta` into
 * `details.confirmation`.
 *
 * Returns `undefined` when the envelope doesn't list the methods, so a caller
 * asks `GET /account/confirmation` rather than offering no way to confirm.
 * Methods this build doesn't know are dropped: a button it can't honour is
 * worse than none.
 */
export function parseConfirmationMeta(
  meta: unknown
): ConfirmationDetails | undefined {
  const envelope = meta as
    { methods?: unknown; two_factor?: unknown } | null | undefined;

  if (!envelope || !Array.isArray(envelope.methods)) return undefined;

  return {
    methods: envelope.methods.filter(isConfirmationMethod),
    twoFactor: envelope.two_factor === true,
  };
}

// =============================================================================
// LARAVEL VALIDATION ERROR PARSING
// =============================================================================

/**
 * Parse Laravel validation errors into structured format
 */
export function parseLaravelValidationErrors(
  errors: Record<string, string[]>
): ValidationErrorInfo[] {
  const validationErrors: ValidationErrorInfo[] = [];

  Object.entries(errors).forEach(([field, messages]) => {
    messages.forEach((message) => {
      validationErrors.push({
        field,
        message,
        rule: extractLaravelRuleFromMessage(message),
        value: undefined, // Laravel doesn't typically return the invalid value
      });
    });
  });

  return validationErrors;
}

/**
 * Extract validation rule from Laravel error message (best effort)
 */
function extractLaravelRuleFromMessage(message: string): string {
  const lowerMessage = message.toLowerCase();

  // Common Laravel validation rules - order matters for specificity
  if (lowerMessage.includes('required')) return 'required';
  if (lowerMessage.includes('email')) return 'email';
  if (lowerMessage.includes('at least') && lowerMessage.includes('characters'))
    return 'min';
  if (lowerMessage.includes('min:') || lowerMessage.includes('minimum'))
    return 'min';
  if (lowerMessage.includes('max:') || lowerMessage.includes('maximum'))
    return 'max';
  if (lowerMessage.includes('unique')) return 'unique';
  if (
    lowerMessage.includes('confirmation') ||
    lowerMessage.includes('confirmed') ||
    lowerMessage.includes('does not match')
  )
    return 'confirmed';
  if (lowerMessage.includes('numeric')) return 'numeric';
  if (lowerMessage.includes('string')) return 'string';
  if (lowerMessage.includes('array')) return 'array';
  if (lowerMessage.includes('boolean')) return 'boolean';
  if (lowerMessage.includes('date')) return 'date';
  if (lowerMessage.includes('file')) return 'file';
  if (lowerMessage.includes('image')) return 'image';
  if (
    lowerMessage.includes('in:') ||
    lowerMessage.includes('allowed values') ||
    lowerMessage.includes('not in the allowed values')
  )
    return 'in';
  if (lowerMessage.includes('not_in:')) return 'not_in';
  if (
    lowerMessage.includes('format is invalid') ||
    lowerMessage.includes('regex:')
  )
    return 'regex';
  if (lowerMessage.includes('size:')) return 'size';
  if (lowerMessage.includes('between:')) return 'between';
  if (lowerMessage.includes('exists:')) return 'exists';
  if (lowerMessage.includes('alpha')) return 'alpha';
  if (lowerMessage.includes('digits')) return 'digits';
  if (lowerMessage.includes('after:')) return 'after';
  if (lowerMessage.includes('before:')) return 'before';
  if (lowerMessage.includes('mimes:')) return 'mimes';
  if (lowerMessage.includes('dimensions:')) return 'dimensions';

  return 'unknown';
}

// =============================================================================
// SERVICE-SPECIFIC ERROR REPORTING
// =============================================================================

/**
 * Create a comprehensive error report for monitoring services
 */
export function prepareErrorReportForService(
  error: ApiError,
  additionalContext: Record<string, unknown> = {}
): ServiceErrorReport {
  const enrichedError = error as ApiError & {
    category?: ErrorCategory;
    severity?: ErrorSeverity;
    url?: string;
    method?: string;
    requestId?: string;
    correlationId?: string;
  };

  return {
    errorId: `err_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`,
    correlationId: enrichedError.correlationId || generateCorrelationId(),
    timestamp: error.timestamp,
    environment: getEnv('NODE_ENV') || 'development',
    service: 'personal-trainer-app-frontend',
    version: getEnv('VITE_APP_VERSION'),
    error: {
      code: error.code,
      message: error.message,
      category: enrichedError.category || 'unknown',
      severity: enrichedError.severity || 'medium',
      status: error.status,
      url: enrichedError.url,
      method: enrichedError.method,
      requestId: enrichedError.requestId,
      details: error.details,
      stack: (error as unknown as Error).stack,
    },
    context: {
      userAgent:
        typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      sessionId: additionalContext.sessionId as string,
      userId: additionalContext.userId as string,
      ip: additionalContext.ip as string,
      referer: typeof window !== 'undefined' ? document.referrer : undefined,
      pathname:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
      search:
        typeof window !== 'undefined' ? window.location.search : undefined,
    },
    metadata: {
      ...additionalContext,
      timestamp: dayjs().valueOf(),
      browser: typeof window !== 'undefined' ? getBrowserMetadata() : undefined,
      screen: typeof window !== 'undefined' ? getScreenMetadata() : undefined,
    },
  };
}

/**
 * Get browser metadata for error reporting
 */
function getBrowserMetadata(): Record<string, unknown> {
  if (typeof window === 'undefined') return {};

  const { navigator } = window;
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: navigator.languages,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    vendor: navigator.vendor,
    maxTouchPoints: navigator.maxTouchPoints,
  };
}

/**
 * Get screen metadata for error reporting
 */
function getScreenMetadata(): Record<string, unknown> {
  if (typeof window === 'undefined') return {};

  const { screen } = window;
  return {
    width: screen.width,
    height: screen.height,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
    colorDepth: screen.colorDepth,
    pixelDepth: screen.pixelDepth,
    devicePixelRatio: window.devicePixelRatio,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  };
}

// =============================================================================
// DEVELOPMENT LOGGING
// =============================================================================

/**
 * Log API errors to console in development mode with rich formatting
 */
export function logApiErrorToConsole(error: ApiError, context?: string): void {
  if (getEnv('NODE_ENV') !== 'development') return;

  const enrichedError = error as ApiError & {
    category?: ErrorCategory;
    severity?: ErrorSeverity;
    url?: string;
    method?: string;
    requestId?: string;
    correlationId?: string;
    retryable?: boolean;
    actionable?: boolean;
    reportable?: boolean;
  };

  const prefix = context ? `🚨 API Error (${context})` : '🚨 API Error';
  console.group(prefix);

  console.error('Error Summary:', {
    code: error.code,
    message: error.message,
    status: error.status,
    severity: enrichedError.severity,
    category: enrichedError.category,
  });

  console.error('Request Context:', {
    url: enrichedError.url,
    method: enrichedError.method,
    requestId: enrichedError.requestId,
    correlationId: enrichedError.correlationId,
  });

  console.error('Error Properties:', {
    retryable: enrichedError.retryable,
    actionable: enrichedError.actionable,
    reportable: enrichedError.reportable,
    timestamp: error.timestamp,
  });

  if (error.details && Object.keys(error.details).length > 0) {
    console.error('Additional Details:', error.details);
  }

  if ((error as unknown as Error).stack) {
    console.error('Stack Trace:', (error as unknown as Error).stack);
  }

  console.groupEnd();
}

/**
 * Log validation errors in a structured format
 */
export function logValidationErrorsToConsole(
  validationErrors: ValidationErrorInfo[]
): void {
  if (getEnv('NODE_ENV') !== 'development') return;

  console.group('🔍 Validation Errors');

  const errorsByField = validationErrors.reduce(
    (acc, error) => {
      if (!acc[error.field]) {
        acc[error.field] = [];
      }
      acc[error.field].push({
        message: error.message,
        rule: error.rule,
        value: error.value,
      });
      return acc;
    },
    {} as Record<
      string,
      Array<{ message: string; rule: string; value?: unknown }>
    >
  );

  Object.entries(errorsByField).forEach(([field, errors]) => {
    console.error(`Field: ${field}`, errors);
  });

  console.groupEnd();
}

// =============================================================================
// SERVICE-SPECIFIC ERROR CHECKS
// =============================================================================

/**
 * Check if error indicates rate limiting with retry information
 */
export function extractRateLimitInfo(error: ApiError): {
  isRateLimited: boolean;
  retryAfterSeconds?: number;
  limit?: number;
  remaining?: number;
  resetTime?: dayjs.Dayjs;
} {
  const isRateLimited =
    error.status === 429 ||
    error.code === API_ERROR_CODES.RATE_LIMITED ||
    error.code === API_ERROR_CODES.TOO_MANY_REQUESTS;

  if (!isRateLimited) {
    return { isRateLimited: false };
  }

  const headers = error.details?.headers as Record<string, string> | undefined;
  const retryAfter = headers?.['retry-after'];
  const limit = headers?.['x-ratelimit-limit'];
  const remaining = headers?.['x-ratelimit-remaining'];
  const reset = headers?.['x-ratelimit-reset'];

  return {
    isRateLimited: true,
    retryAfterSeconds: retryAfter ? parseInt(retryAfter, 10) : undefined,
    limit: limit ? parseInt(limit, 10) : undefined,
    remaining: remaining ? parseInt(remaining, 10) : undefined,
    resetTime: reset ? dayjs.unix(parseInt(reset, 10)) : undefined,
  };
}

/**
 * Check if error is related to authentication and extract auth context
 */
export function extractAuthErrorInfo(error: ApiError): {
  isAuthError: boolean;
  requiresLogin?: boolean;
  requiresRefresh?: boolean;
  requiresTwoFactor?: boolean;
} {
  const isAuthError =
    error.status === 401 ||
    error.code === API_ERROR_CODES.UNAUTHENTICATED ||
    error.code === API_ERROR_CODES.TOKEN_EXPIRED ||
    error.code === API_ERROR_CODES.TOKEN_INVALID ||
    error.code === API_ERROR_CODES.TOKEN_MISSING;

  if (!isAuthError) {
    return { isAuthError: false };
  }

  return {
    isAuthError: true,
    requiresLogin:
      error.code === API_ERROR_CODES.UNAUTHENTICATED ||
      error.code === API_ERROR_CODES.TOKEN_MISSING,
    requiresRefresh: error.code === API_ERROR_CODES.TOKEN_EXPIRED,
    requiresTwoFactor: error.code === API_ERROR_CODES.TWO_FACTOR_REQUIRED,
  };
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Generate a correlation ID for error tracking
 */
function generateCorrelationId(): string {
  return `corr_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Extract meaningful context from an ApiError for debugging
 */
export function extractDebugContext(error: ApiError): Record<string, unknown> {
  const enrichedError = error as ApiError & {
    url?: string;
    method?: string;
    requestId?: string;
    correlationId?: string;
  };

  return {
    code: error.code,
    message: error.message,
    status: error.status,
    url: enrichedError.url,
    method: enrichedError.method,
    requestId: enrichedError.requestId,
    correlationId: enrichedError.correlationId,
    timestamp: error.timestamp,
    details: error.details,
    stack: (error as unknown as Error).stack
      ?.split('\n')
      .slice(0, 3)
      .join('\n'), // First 3 lines only
  };
}
